if (!customElements.get("op-related-products")) {
    customElements.define("op-related-products", class extends OpAlgoliaUtilities {
        cartProdVariants = [];
        fallbackProductHandles = [];
        hideIfEmpty = false;
        hits = [];
        isLoading = false;
        isReady = false;
        lastTrackedViewKey = "";
        maxRecommendations = 3;
        productCards = [];
        preserveRecommendationSeedOnNextAttributeChange = false;
        recommendationObserver = null;
        recommendationObjectID = "";
        shopRoot = null;
        sectionContainer = null;
        swiper = null;
        swiperWrapper = null;
        swiperPagination = null;
        staticProductHandles = [];
        showOnlyAvailable = false;
        unsubscribeCartUpdate = null;
        hasEnteredViewport = false;

        static get observedAttributes() {
            return ["cart-product-variants", "max-recomandations"];
        }

        connectedCallback() {
            this.shopRoot = window.Shopify?.routes?.root;
            this.showOnlyAvailable = this.dataset.showOnlyAvailable === "true";
            this.sectionContainer = this.closest("#shopify-section-" + this.dataset.sectionId);
            this.swiperWrapper = this.querySelector(".swiper-wrapper");
            this.swiperPagination = this.querySelector("[data-pagination]");
            this.hideIfEmpty = this.dataset.hideIfEmpty === "true";
            this.maxRecommendations = this.dataset.maxRecommendations

            if (window.Swiper) {
                this.swiper = new window.Swiper(this, {
                    slidesPerView: 1,
                    loop: false,
                    spaceBetween: 8,
                    pagination: {
                        el: this.swiperPagination,
                        clickable: true,
                        bulletClass: this.dataset.bulletClass,
                        bulletActiveClass: this.dataset.bulletActiveClass
                    },
                    breakpoints: {
                        600: {
                            slidesPerView: 1.7,
                            spaceBetween: 24,
                        },
                        1024: {
                            slidesPerView: 3,
                            spaceBetween: 24,
                        },
                        1280: {
                            slidesPerView: 3,
                            spaceBetween: 24,
                        }
                    }
                });
            }

            this.unsubscribeCartUpdate = this.subscribeToCartUpdate((event) => {
                const items = event?.cartData?.items;
    
                if (Array.isArray(items)) {
                    this.preserveRecommendationSeedOnNextAttributeChange = true;
                    this.setAttribute("cart-product-variants", items.map(item => item.variant_id).join(","));
                }
            });

            if (!this.recommendationObjectID) {
                this.recommendationObjectID = this.cartProdVariants[0] || "";
            }

            this.isReady = true;
            this.observeRecommendationsViewport();
        }

        disconnectedCallback() {
            this.recommendationObserver?.disconnect();
            this.swiper?.destroy();

            if (typeof this.unsubscribeCartUpdate === "function") {
                this.unsubscribeCartUpdate();
            }

            this.isReady = false;
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue === newValue) {
                return;
            }

            switch (name) {
                case "cart-product-variants":
                    const preserveRecommendationSeed = this.preserveRecommendationSeedOnNextAttributeChange;
                    this.preserveRecommendationSeedOnNextAttributeChange = false;
                    this.cartProdVariants = newValue ? newValue.split(",") : [];

                    if (!preserveRecommendationSeed || !this.recommendationObjectID) {
                        this.recommendationObjectID = this.cartProdVariants[0] || "";
                    }

                    if (this.hasEnteredViewport) {
                        this.loadAndMarkRecommendations();
                    }
                    break;
                case "max-recomandations":
                    this.maxRecommendations = parseInt(newValue) || this.maxRecommendations;
                    break;
            }
        }

        observeRecommendationsViewport () {
            if (this.hasEnteredViewport) {
                return;
            }

            if (typeof window.IntersectionObserver !== "function") {
                this.hasEnteredViewport = true;
                this.loadAndMarkRecommendations();
                return;
            }

            this.recommendationObserver?.disconnect();
            this.recommendationObserver = new IntersectionObserver((entries, observer) => {
                if (!entries.some(entry => entry.isIntersecting)) {
                    return;
                }

                this.hasEnteredViewport = true;
                observer.disconnect();
                this.loadAndMarkRecommendations();
            }, {
                rootMargin: "0px 0px 200px 0px",
                threshold: 0.1
            });

            this.recommendationObserver.observe(this);
        }

        async loadAndMarkRecommendations () {
            try {
                await this.loadRecommendedProducts();
            } catch (error) {
                console.error("Error loading recommendation carousel:", error);
            } finally {
                this.toggleAttribute("data-loaded", true);
            }
        }

        clearProductCards () {
            if (this.productCards?.length > 0) {
                this.swiper?.removeAllSlides();
                this.productCards.forEach(card => card.remove());
                this.productCards.length = 0;
            }

            this.hits.length = 0;
        }    
        
        fetchRelatedProducts(indexName, objectIDs, maxRecommendations, showOnlyAvailable, model = "related-products", threshold = 0) {
            const url = this.apiUrl ? new URL(`${this.apiUrl}/1/indexes/*/recommendations`) : null;
            const requests = [];
            let facetFilters = [];
            
            indexName = indexName || this.productsIndex;

            if (!url) {
                console.error("Algolia API URL is not set. Configuration:", this.algoliaConfig);
                return;
            }

            // objectID is the product ID taken from the hit object
            if (!objectIDs) {
                console.error("objectID is required");
                return;
            } 
            
            if (!indexName) {
                console.error("indexName is required");
                return;
            } 
            
            if (!model) {
                console.error("model is required");
                return;
            }
            
            url.searchParams.set("x-algolia-api-key", this.algoliaApiKey);
            url.searchParams.set("x-algolia-application-id", this.algoliaAppId);

            if (showOnlyAvailable) {
                facetFilters.push("inventory_available:true");
            }

            if(this.cartProdVariants.length > 0) {
                this.cartProdVariants.forEach((element, index) => {
                    facetFilters.push(`objectID:-${element}`);
                })
            }

            if (Array.isArray(objectIDs)) {
                objectIDs.forEach(id => requests.push({
                    indexName,
                    model,
                    queryParameters: { facetFilters },
                    objectID: id,
                    threshold
                }));
            } else {
                requests.push({
                    indexName,
                    model,
                    queryParameters: { facetFilters },
                    objectID: objectIDs,
                    threshold
                });
            }

            if (maxRecommendations) {
                requests.forEach(req => req.maxRecommendations = maxRecommendations);
            }

            return fetch(url, {
                body: JSON.stringify({ requests }),
                method: "POST",
            })
            .then(response => response.json())
            .then(data => {
                return data;
            })
            .catch(error => {
                console.error('Error:', error);
                throw error;
            });
        }

        async loadRecommendedProducts () {
            if (this.isLoading || !this.isReady) {
                return;
            }

            this.isLoading = true;

            try {
                this.staticProductHandles = this.parseDatasetHandles(this.dataset.staticProductHandles);
                this.fallbackProductHandles = this.parseDatasetHandles(this.dataset.fallbackProductHandles);
                const recommendationObjectID = this.recommendationObjectID || this.cartProdVariants[0];

                if (this.staticProductHandles.length > 0) {
                    this.clearProductCards();
                    await this.showStaticProducts(this.staticProductHandles);
                    this.toggleSectionEmpty(this.productCards.length === 0);
                    return;
                }

                if (recommendationObjectID) {
                    try {
                        const data = await this.fetchRelatedProducts(
                            this.productsIndex,
                            recommendationObjectID,
                            parseInt(this.maxRecommendations),
                            this.showOnlyAvailable
                        );

                        if (data?.results) {
                            await this.updateProductCards(data.results);
                        }
                    }
                    catch (error) {
                        console.error('Error fetching related products:', error);
                    }
                }

                if (this.productCards.length === 0 || !recommendationObjectID) {
                    this.clearProductCards();
                    await this.showFallbackProducts();
                }

                this.toggleSectionEmpty(this.productCards.length === 0);
                this.trackRecommendationView();
            } finally {
                this.isLoading = false;
            }
        }

        parseDatasetHandles (rawHandles = "") {
            const handles = [];

            rawHandles?.split(",").forEach(handle => {
                handle = handle.trim();
                
                if (handle !== "") {
                    handles.push(handle);
                }
            });

            return handles;
        }

        showFallbackProducts () {
            const cardPromises = [];

            this.fallbackProductHandles.forEach(handle => {
                const promise = this.addProductCard({ handle });
                cardPromises.push(promise);
            });

            return Promise.allSettled(cardPromises);
        }

        showStaticProducts () {
            const cardPromises = [];

            this.staticProductHandles.forEach(handle => {
                const promise = this.addProductCard({ handle });
                cardPromises.push(promise);
            });

            return Promise.allSettled(cardPromises);
        }

        toggleSectionEmpty (status) {
            this.toggleAttribute("data-empty", status);
            
            if (this.hideIfEmpty) {
                this.setSectionHidden(status);
            }
        }

        updateProductCards (results = []) {
            const objIDs = new Set();
            const cardPromises = [];

            this.hits.length = 0;
            
            this.clearProductCards();

            results.some(result => {
                const resultHits = result.hits || [];

                for (let hit of resultHits) {
                    if (this.hits.length >= this.maxRecommendations) {
                        return true;
                    }

                    if (hit.handle && !this.cartProdVariants.includes(hit.objectID) && !objIDs.has(hit.objectID)) {
                        if (hit.inventory_available || !this.showOnlyAvailable) {
                            this.hits.push(hit);
                            objIDs.add(hit.objectID);
                            cardPromises.push(this.addProductCard(hit));
                        }
                    }
                }

                return false;
            });

            return Promise.allSettled(cardPromises);
        }

        async addProductCard(hit) {
            const productCardUrl = `${this.shopRoot}products/${hit.handle}?view=op-cart-product-card`
      
            try {
                const response = await fetch(productCardUrl);
                
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
      
                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const cardElement = doc.querySelector("#op-cart-product-card");

                if (!cardElement) {
                    throw new Error(`Product card not found in ${productCardUrl}`);
                }

                if (typeof hit.trackSearchAttribution === "function") {
                    cardElement.addEventListener("click", () => {
                        hit.trackSearchAttribution();
                    });
                }

                const slideElement = cardElement.children[0];
                this.bindRecommendationClick(slideElement, hit);
                this.appendSlide(slideElement);
            } catch (error) {
                console.error('Error fetching product card:', error);
            }
        }

        bindRecommendationClick (element, hit) {
            if (!element || !hit?.objectID || element.dataset.recommendationClickBound === "true") {
                return;
            }

            element.dataset.recommendationClickBound = "true";
            element.addEventListener("click", (event) => {
                if (!event.target.closest("a[href], button")) {
                    return;
                }

                this.trackRecommendationClick(hit);
            });
        }

        trackRecommendationView () {
            try {
                if (!this.hasEnteredViewport || typeof window.aa !== "function" || !this.productsIndex) {
                    return;
                }

                const objectIDs = this.hits
                    .map(hit => hit?.objectID)
                    .filter(Boolean);

                if (objectIDs.length === 0) {
                    return;
                }

                const viewKey = objectIDs.join(",");

                if (viewKey === this.lastTrackedViewKey) {
                    return;
                }

                this.lastTrackedViewKey = viewKey;
                window.aa("viewedObjectIDs", {
                    eventName: "Recommendation Viewed",
                    index: this.productsIndex,
                    objectIDs
                });
            } catch (error) {
                console.error("Recommendation view tracking failed:", error);
            }
        }

        trackRecommendationClick (hit) {
            try {
                if (typeof window.aa !== "function" || !this.productsIndex || !hit?.objectID) {
                    return;
                }

                window.aa("clickedObjectIDs", {
                    eventName: "Recommendation Clicked",
                    index: this.productsIndex,
                    objectIDs: [hit.objectID]
                });
            } catch (error) {
                console.error("Recommendation click tracking failed:", error);
            }
        }

        appendSlide (element) {
            if (!this.swiperWrapper) {
                this.swiperWrapper = document.createElement("div");
                this.swiperWrapper.classList.add("swiper-wrapper");
                this.prepend(this.swiperWrapper);
            }

            if (this.swiper) {
                element.classList.add("swiper-slide");
                this.swiper.appendSlide(element);
            } else {
                this.append(element);

                this.swiperWrapper?.style.setProperty("display", "none");
                this.swiperPagination?.style.setProperty("display", "none");
            }

            // remove all btn "notify me "
            element.querySelectorAll('.restock-alerts-notify-button').forEach(btn => btn.remove());
            // Observe after element construction - notify me insert element after construction
            const observer = new MutationObserver(() => {
                element.querySelectorAll('.restock-alerts-notify-button').forEach(btn => btn.remove());
            });
            observer.observe(element, { childList: true, subtree: true });
            
            this.productCards.push(element);
        }

        setSectionHidden (status) {
            if (!this.sectionContainer) return;
            status ? this.sectionContainer.style.setProperty("display", "none") : this.sectionContainer.style.removeProperty("display");
        }

        subscribeToCartUpdate (callback) {
            const cartUpdateEvent = typeof PUB_SUB_EVENTS !== "undefined" ? PUB_SUB_EVENTS?.cartUpdate : null;
            return typeof window.subscribe === "function" ? window.subscribe(cartUpdateEvent || "cart-update", callback) : null;
        }
    });
}
